import { createContext, useContext, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Sparkles, ArrowRight } from "lucide-react";

const GuestGateContext = createContext({ requireAuth: () => true });

export const GuestGateProvider = ({ children }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [action, setAction] = useState("continue");

  const requireAuth = useCallback((actionLabel = "continue") => {
    if (user) return true;
    setAction(actionLabel);
    setOpen(true);
    return false;
  }, [user]);

  return (
    <GuestGateContext.Provider value={{ requireAuth }}>
      {children}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#15151F] border-[#1A1A24] text-white max-w-md" data-testid="guest-gate-modal">
          <DialogHeader>
            <div className="w-12 h-12 rounded-full bg-violet-500/20 flex items-center justify-center mb-3">
              <Sparkles className="w-6 h-6 text-violet-400" />
            </div>
            <DialogTitle className="text-xl">Join AIxTribe to {action}</DialogTitle>
            <DialogDescription className="text-gray-400 text-sm leading-relaxed">
              Create your free account to unlock the full AIxTribe experience — 23+ AI tools, jobs, courses, events, and India&apos;s most active AI community.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2 pt-2">
            <Button
              className="bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white hover:opacity-90"
              onClick={() => { setOpen(false); navigate("/register"); }}
              data-testid="guest-gate-register-btn"
            >
              Get Started Free <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
            <Button
              variant="ghost"
              className="text-gray-300 hover:text-white hover:bg-[#1A1A24]"
              onClick={() => { setOpen(false); navigate("/login"); }}
              data-testid="guest-gate-login-btn"
            >
              I already have an account · Log in
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </GuestGateContext.Provider>
  );
};

export const useGuestGate = () => useContext(GuestGateContext);
